import { AxiosError } from "axios";
import { useEffect, useState } from "react";

import { PaymentInfo } from "@/types/Invoice";
import { getErrorMessage } from "@/utils/getErrorMessage";
import {
  formatCurrency,
  formatTimestampDate,
  getTodaysDate
} from "@/utils/InvoicePay";
import {
  describePaymentMethodApi,
  getPaymentDetails,
  getPaymentDetailsByInvoiceNo
} from "@/utils/PaymentsService/PaymentMethodApis";

interface PaymentDetailsParams {
  invoiceNo?: string;
  paymentId?: string;
}

export const usePaymentDetails = ({
  invoiceNo,
  paymentId
}: PaymentDetailsParams) => {
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!invoiceNo && !paymentId) return;

    (async () => {
      try {
        setIsLoading(true);
        let paymentMethod = "••••";
        const response = invoiceNo
          ? await getPaymentDetailsByInvoiceNo(invoiceNo)
          : await getPaymentDetails(paymentId);

        if (response.status === 200) {
          const paymentDetails = response.data.paymentDetails;
          const paymentMethodId = paymentDetails.paymentMethodId;
          const paymentMethodResponse =
            await describePaymentMethodApi(paymentMethodId);

          if (paymentMethodResponse.status === 200) {
            const methodData = paymentMethodResponse.data.paymentMethodDetails;
            const lastFour = methodData.paymentMethodInfo.lastFour;
            paymentMethod =
              methodData.paymentMethodType === "CARD"
                ? `Credit card *${lastFour}`
                : `Bank account *${lastFour}`;
          }

          const paymentDate = paymentId
            ? getTodaysDate()
            : formatTimestampDate(paymentDetails.updatedAt);

          setPaymentInfo({
            confirmationNum: paymentDetails.externalPaymentId,
            invoiceNums: paymentDetails.invoiceId,
            paymentDate: paymentDate,
            paymentMethod: paymentMethod,
            amount: formatCurrency(paymentDetails.amount)
          });
        }
      } catch (err: unknown) {
        if (err instanceof AxiosError && err.response?.data.errorCode) {
          const message = getErrorMessage(err.response?.data.errorCode);
          setError(message);
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, [invoiceNo, paymentId]);

  return { paymentInfo, isLoading, error };
};
